import { AxiosResponse } from 'axios';

import { API } from '../../utils/axios';

export interface ConsentResponse {
  statusCode: number;
  message: string;
  resource: ConsentConfig[];
}

export interface ConsentConfig {
  id: number;
  config_type: string;
  service_type: string;
  config_value: ConsentValue;
}

export interface ConsentValue {
  consent_type: ConsentType;
  language: 'en' | 'es';
  title: string;
  content: string;
  version: string;
  effective_date: string;
}

export type ConsentType =
  | 'electronic_communications'
  | 'data_use_aggregation'
  | 'treatment_hipaa'
  | 'recording_ai_tools'
  | 'electronic_prescription'
  | 'minor_consent';

class ConsentService {
  /**
   * Get all legal consents for patient portal
   * @param language - Language code ('en' or 'es')
   * @returns Promise with consent configurations
   */
  static getConsents(language: 'en' | 'es' = 'en') {
    return API.get<any, AxiosResponse<ConsentResponse>>(
      `/v1/auth/config_values?serviceType=Web&configType=legal_consent`,
    ).then((res) => {
      // Filter consents by language
      const filteredResource = res.data.resource
        .filter((consent) => consent.config_value.language === language)
        .sort((a, b) => {
          // Sort by consent type for consistent ordering
          const order: ConsentType[] = [
            'treatment_hipaa',
            'electronic_communications',
            'data_use_aggregation',
            'recording_ai_tools',
            'electronic_prescription',
            'minor_consent',
          ];
          return (
            order.indexOf(a.config_value.consent_type) - order.indexOf(b.config_value.consent_type)
          );
        });

      return {
        ...res.data,
        resource: filteredResource,
      };
    });
  }

  /**
   * Get a specific consent by type and language
   * @param consentType - The type of consent to retrieve
   * @param language - Language code ('en' or 'es')
   * @returns Promise with single consent configuration
   */
  static getConsentByType(consentType: ConsentType, language: 'en' | 'es' = 'en') {
    return this.getConsents(language).then((response) => {
      const consent = response.resource.find((c) => c.config_value.consent_type === consentType);
      return consent || null;
    });
  }
}

export default ConsentService;
